import i18next from 'core/i18n';
import React, { useState } from 'react';

import { FinancingRole, FinancingUser } from '@indigo-ag/schema';

import { EnhancedTable, HeadCell, Scope, createStylesHook } from 'shared';

import { UserUtil } from 'shared/utils/model-utils/user.util';
import { DeleteUserDialog } from './DeleteUserDialog/DeleteUserDialog';
import { UpdateUserPreferencesDialog } from './UpdateUserPreferencesDialog/UpdateUserPreferencesDialog';
import { UserActions } from './UserActions/UserActions';

const useStyles = createStylesHook({
  textTransformCap: {
    textTransform: 'capitalize'
  }
});

interface AddUserProps {
  users: FinancingUser[];
  roles: FinancingRole[];
  myEmail: string;
}

interface MappedUser extends FinancingUser {
  accessLevel?: string;
  roleName?: string;
}

export const UserList: React.FunctionComponent<AddUserProps> = ({ users, roles, myEmail }) => {
  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * State
   */
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [updateUserRoleDialogOpen, setUpdateUserRoleDialogOpen] = useState(false);
  const [userSelected, setUserSelected] = useState<FinancingUser | null>(null);

  /**
   * Dialogs
   */
  const openDeleteUserDialog = (user: FinancingUser) => {
    setUserSelected(user);
    setDeleteUserDialogOpen(true);
  };
  const closeDeleteUserDialog = () => {
    setDeleteUserDialogOpen(false);
    setUserSelected(null);
  };

  const openUpdateUserRoleDialog = (user: FinancingUser) => {
    setUserSelected(user);
    setUpdateUserRoleDialogOpen(true);
  };
  const closeUpdateUserRoleDialog = () => {
    setUpdateUserRoleDialogOpen(false);
    setUserSelected(null);
  };

  const renderUserScope = (user: FinancingUser) => {
    const scopePermission = UserUtil.getUserScope(user);

    switch (scopePermission) {
      case Scope.GLOBAL:
        return 'super';
      case Scope.BR:
        return i18next.t('br');
      default:
        return '-';
    }
  };

  const usersHeadCells: HeadCell<MappedUser & { actions: unknown }>[] = [
    {
      id: 'email',
      label: i18next.t('user'),
      mapper: (user: FinancingUser) => <span>{user.email}</span>
    },
    {
      id: 'accessLevel',
      label: i18next.t('accessLevel'),
      mapper: (user: MappedUser) => <span className={classes.textTransformCap}>{user.accessLevel}</span>
    },
    {
      id: 'roleName',
      label: i18next.t('role'),
      mapper: (user: MappedUser) => <span className={classes.textTransformCap}>{user.roleName}</span>
    },
    {
      id: 'actions',
      label: i18next.t('actions'),
      unSort: true,
      mapper: (user: MappedUser) => (
        <UserActions
          user={user}
          remove={openDeleteUserDialog}
          update={openUpdateUserRoleDialog}
          disabled={user.email === myEmail}
        />
      )
    }
  ];
  const mappedUsers = users.map(user => ({
    ...user,
    accessLevel: renderUserScope(user),
    roleName: user.role.name
  }));
  return (
    <>
      <EnhancedTable rows={mappedUsers} headCells={usersHeadCells} sort />
      <DeleteUserDialog open={deleteUserDialogOpen} userToDelete={userSelected} closeDialog={closeDeleteUserDialog} />
      <UpdateUserPreferencesDialog
        userToUpdate={userSelected}
        roles={roles}
        open={updateUserRoleDialogOpen}
        closeDialog={closeUpdateUserRoleDialog}
      />
    </>
  );
};
